import React, { ReactNode } from 'react';
import { FaqItem, FictionalIcon } from '../../customFrontend/parts';

/**
 * The city pages' copy. Each city is written for that city (never a template
 * with the name swapped: search engines treat those as doorway pages), and
 * none of it is shared with milfscout.net.
 */
export interface CityContent {
  path: string;
  slug: string;
  name: string;
  /** State or territory, on the city links */
  state: string;
  /** One line on the home page's city tiles */
  teaser: string;
  kicker: string;
  h1: string;
  intro: ReactNode;
  sections: { title: string; body: ReactNode }[];
  faq: FaqItem[];
  cta: { title: string; text: string; button: string };
}

const disclosure = (city: string): FaqItem => ({
  q: `Could I meet the women behind the ${city} profiles?`,
  a: (
    <p>
      No. MatureFlirt uses fictional profiles whose purpose is flirting with users. Each one shows a person
      icon <FictionalIcon /> and physical contact with them is not possible. MatureFlirt is a chat service for
      entertainment and does not arrange meetings.
    </p>
  ),
});

const pricing: FaqItem = {
  q: 'How much does it cost?',
  a: <p>Joining costs nothing and your first messages are free. Messages, flirts and photos after that are paid with credits, and you only buy a package when you decide to carry on.</p>,
};

const noCard: FaqItem = {
  q: 'Do I have to give card details to join?',
  a: <p>No. Registration never asks for a card. You only enter payment details if you choose to buy credits later.</p>,
};

const privacy: FaqItem = {
  q: 'Who can see that I am a member?',
  a: <p>Other members only see the nickname you choose. Your email address and real name stay private, and nothing is shared on social media.</p>,
};

export const CITIES: Record<string, CityContent> = {
  '/sydney': {
    path: '/sydney',
    slug: 'sydney',
    name: 'Sydney',
    state: 'NSW',
    teaser: 'Harbourside champagne, Paddington terraces and a flirt on the Manly ferry.',
    kicker: 'Harbour city glamour',
    h1: 'Mature chat in Sydney',
    intro: (
      <p>
        Sydney has never been shy about its looks, and neither are the confident women who have spent decades
        enjoying it. They know which harbourside bar pours the best champagne, which beach is quiet on a weekday
        and how to hold a conversation that is clever, warm and a little bit daring. MatureFlirt brings that style
        of cougar and MILF chat to your phone, with free signup and your first messages free.
      </p>
    ),
    sections: [
      {
        title: 'From Double Bay to the Inner West',
        body: (
          <>
            <p>
              Every part of Sydney has its own kind of sophistication. In Double Bay and Mosman it is long lunches and
              linen; in Paddington and Woollahra it is galleries, boutiques and terrace houses with iron lace
              balconies; in Balmain and Newtown it is wine bars, bookshops and a little more attitude. Potts Point
              keeps the late tables, and Barangaroo the after-work crowd with a view of the bridge.
            </p>
            <p>
              Conversations on MatureFlirt tend to follow the same rhythm: a playful message at lunch, a reply after
              work, and a longer, warmer chat once the evening settles in.
            </p>
          </>
        ),
      },
      {
        title: 'Ferries, trains and the coastal walk',
        body: (
          <>
            <p>
              The Manly ferry is half an hour of harbour views, and plenty of passengers spend it on their phones. So
              do commuters on the North Shore line and anyone recovering from the Bondi to Bronte walk with a coffee
              in hand. A short, flirty exchange fits neatly into those gaps in the day.
            </p>
            <h3>Winter in the city</h3>
            <p>
              When Vivid lights up the Opera House and the evenings turn cool, Sydney moves indoors. That is when the
              conversations grow longer, and an experienced woman&apos;s sense of humour comes into its own.
            </p>
          </>
        ),
      },
      {
        title: 'How to start a Sydney chat',
        body: (
          <p>
            Keep it confident and genuine. Mature women appreciate a man who asks a real question rather than
            reaching for a line, and a compliment about wit lands better than one about looks. Send a flirt if you
            want to say hello in one tap, and let the conversation build from there.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Can I chat from anywhere in greater Sydney?',
        a: <p>Yes. Members chat from the Northern Beaches to Campbelltown and from Parramatta to the Eastern Suburbs. MatureFlirt runs in your phone&apos;s browser, so all you need is a signal.</p>,
      },
      noCard,
      disclosure('Sydney'),
      pricing,
    ],
    cta: { title: 'Sydney after dark, on your phone', text: 'Create your free profile and send your first messages free.', button: 'Start chatting in Sydney' },
  },
  '/melbourne': {
    path: '/melbourne',
    slug: 'melbourne',
    name: 'Melbourne',
    state: 'VIC',
    teaser: 'Laneway wine bars, rooftop evenings and four seasons in a single day.',
    kicker: 'Laneways, culture and good conversation',
    h1: 'Mature chat in Melbourne',
    intro: (
      <p>
        Melbourne takes its coffee, its football and its conversation seriously. The women who have made the city
        theirs over the years tend to be cultured, opinionated and very good company, as comfortable at the NGV as
        at a corner table in a laneway bar. MatureFlirt brings MILF and cougar chat with that Melbourne character
        to your phone: free to join, first messages free.
      </p>
    ),
    sections: [
      {
        title: 'Laneways, rooftops and Chapel Street',
        body: (
          <>
            <p>
              A Melbourne evening rarely happens on a main street. It starts in a laneway off Flinders Lane or
              Degraves Street, climbs to a rooftop bar when the weather behaves and ends somewhere in Fitzroy or
              Collingwood with live music and a glass of Yarra Valley pinot. South of the river, Chapel Street in
              South Yarra and the elegant streets of Toorak and Armadale have a polish of their own.
            </p>
            <p>
              It is a city that enjoys a slow build, which is exactly how a good chat on MatureFlirt works.
            </p>
          </>
        ),
      },
      {
        title: 'Trams, seasons and the weekend',
        body: (
          <>
            <p>
              The tram along St Kilda Road, the train in from the bayside suburbs, a coffee queue on a Saturday
              morning: Melbourne has plenty of small moments to fill, and a teasing message fills them well. At the
              weekend the city heads to the bathing boxes at Brighton, the markets at Prahran or the MCG, and the
              phone goes along.
            </p>
            <h3>Four seasons in a day</h3>
            <p>
              Melbourne weather changes its mind by the hour, so the locals have learned to make the most of an
              evening indoors. Cold, wet winter nights are when the longest and most interesting conversations
              happen.
            </p>
          </>
        ),
      },
      {
        title: 'Spring racing and festival nights',
        body: (
          <p>
            From the Comedy Festival in autumn to the Spring Racing Carnival, when Flemington fills with fascinators
            and the city dresses up for the Melbourne Cup, there is always an occasion to talk about. Use it. Ask
            about her favourite exhibition or the best bar she has found this year; Melbourne women respond to
            curiosity far more than to flattery.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Does it work outside inner Melbourne?',
        a: <p>Yes. Members chat from Frankston and the Mornington Peninsula to the outer east and Geelong. Wherever you are in Victoria, all you need is your phone.</p>,
      },
      privacy,
      disclosure('Melbourne'),
      pricing,
    ],
    cta: { title: 'A Melbourne evening, well spent', text: 'Sign up free; your first messages cost nothing.', button: 'Start chatting in Melbourne' },
  },
  '/brisbane': {
    path: '/brisbane',
    slug: 'brisbane',
    name: 'Brisbane',
    state: 'QLD',
    teaser: 'CityCat sunsets, James Street style and warm subtropical evenings.',
    kicker: 'The river city',
    h1: 'Mature chat in Brisbane',
    intro: (
      <p>
        Brisbane has grown up fast, from a sleepy river town into a confident subtropical city, and its mature women
        have watched it happen with a drink in hand on a breezy verandah. They are relaxed, sun-kissed and direct,
        and they like a conversation that is easy and a little flirtatious. MatureFlirt brings Brisbane cougar chat
        to your phone, free to join and with your first messages free.
      </p>
    ),
    sections: [
      {
        title: 'James Street, New Farm and Howard Smith Wharves',
        body: (
          <>
            <p>
              Fortitude Valley&apos;s James Street is Brisbane at its most polished, with boutiques, long lunches and
              cocktail bars under the frangipani. A short walk away, New Farm Park and the Powerhouse draw a relaxed
              weekend crowd, and Howard Smith Wharves sits under the Story Bridge with the river lapping at the
              tables. Across the water, South Bank and West End keep the evenings lively.
            </p>
            <p>
              Brisbane does sunset well. Many conversations on MatureFlirt start just as the light turns gold.
            </p>
          </>
        ),
      },
      {
        title: 'Queenslanders, CityCats and storms',
        body: (
          <>
            <p>
              The old timber houses on stilts in Paddington, Ashgrove and Bulimba are built for the climate, with wide
              verandahs that catch the evening breeze. The CityCat ferries glide past them on the way to the city,
              and the ride is a perfect stretch of time for a flirt or two.
            </p>
            <h3>Summer afternoons</h3>
            <p>
              In summer the afternoon storms roll in over the hills like clockwork. When the rain hammers the tin
              roof and the city waits it out, a warm conversation on your phone is the best way to pass the hour.
            </p>
          </>
        ),
      },
      {
        title: 'Jacaranda season',
        body: (
          <p>
            Every October the jacarandas turn the suburbs purple, and the city seems to slow down to look at them.
            Brisbane&apos;s pace suits mature chat: unhurried, friendly and open. Be easy-going in your openers, ask
            about her favourite spot by the river and let the conversation find its own warmth.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Can I chat from Ipswich, Logan or Redcliffe?',
        a: <p>Of course. Members chat from right across South East Queensland. MatureFlirt runs in any phone browser, so there is nothing to install.</p>,
      },
      noCard,
      disclosure('Brisbane'),
    ],
    cta: { title: 'Brisbane evenings, warmer still', text: 'Create a free profile; your first messages are free.', button: 'Start chatting in Brisbane' },
  },
  '/perth': {
    path: '/perth',
    slug: 'perth',
    name: 'Perth',
    state: 'WA',
    teaser: 'Cottesloe sunsets, Kings Park views and long evenings on the Swan.',
    kicker: 'Sunsets over the Indian Ocean',
    h1: 'Mature chat in Perth',
    intro: (
      <p>
        Perth is the sunniest capital in Australia and the only one where the sun sets over the ocean. The city is
        relaxed, outdoorsy and a long way from everywhere else, and its mature women carry that independence
        well: tanned, self-assured and not in the least interested in games. MatureFlirt brings that kind of
        confident flirt chat to your phone, with free signup and your first messages free.
      </p>
    ),
    sections: [
      {
        title: 'Cottesloe, Fremantle and the river',
        body: (
          <>
            <p>
              Summer evenings in Perth belong to the coast. The lawns above Cottesloe Beach fill up as the sun drops
              into the Indian Ocean, Fremantle&apos;s South Terrace hums with cafés and old pubs, and the restaurants
              along the Swan River at Applecross and Crawley make the most of the view. Inland, Mount Lawley&apos;s
              Beaufort Street and the small bars of Northbridge keep going later.
            </p>
            <p>
              When the Fremantle Doctor, the afternoon sea breeze, finally cools the city down, the evening chats
              begin.
            </p>
          </>
        ),
      },
      {
        title: 'Kings Park and Rottnest weekends',
        body: (
          <>
            <p>
              Kings Park looks out over the whole city and the river, and a walk there is a local ritual. At the
              weekend Perth heads to Rottnest Island by ferry, south to the wineries of Margaret River or simply to
              the backyard, and a conversation that started on Friday can easily run all weekend.
            </p>
            <h3>On Perth time</h3>
            <p>
              Being two or three hours behind the east coast has its advantages. When Sydney has gone to bed, Perth
              is still chatting, and the late evening is often the liveliest time on MatureFlirt here.
            </p>
          </>
        ),
      },
      {
        title: 'Talking to a Perth woman',
        body: (
          <p>
            Perth is direct and down to earth. A sincere hello and a real question about her weekend works better
            than a polished line, and a little humour about the isolation always helps. If words fail you, send a
            flirt; it says hello in one tap.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Is MatureFlirt available across Western Australia?',
        a: <p>Yes. Members chat from Joondalup and Mandurah to the hills and further afield in regional WA. All you need is a phone with a signal.</p>,
      },
      privacy,
      disclosure('Perth'),
      pricing,
    ],
    cta: { title: 'The sun is setting over Cottesloe', text: 'Join free and send your first messages free.', button: 'Start chatting in Perth' },
  },
  '/adelaide': {
    path: '/adelaide',
    slug: 'adelaide',
    name: 'Adelaide',
    state: 'SA',
    teaser: 'Festival season, Barossa reds and the tram to Glenelg.',
    kicker: 'The festival city',
    h1: 'Mature chat in Adelaide',
    intro: (
      <p>
        Adelaide is elegant, easy to get around and surrounded by some of the best wine country in the world. It is
        a city that likes to take its time, and its mature women are refined, generous with their laughter and
        excellent at a long conversation over a glass of something local. MatureFlirt brings Adelaide MILF chat to
        your phone: free to join, and your first messages are free.
      </p>
    ),
    sections: [
      {
        title: 'Rundle Street, Norwood and the Central Market',
        body: (
          <>
            <p>
              The East End around Rundle Street has Adelaide&apos;s busiest terraces, and Norwood Parade has the
              boutiques and cafés of the eastern suburbs. Peel Street and Leigh Street are lined with small bars, and
              Saturday morning at the Central Market is as social as any evening out. North Terrace, with its
              galleries and sandstone, gives the city its grand side.
            </p>
            <p>
              It is the kind of place where everyone seems to know someone you know, so a discreet nickname is
              worth having.
            </p>
          </>
        ),
      },
      {
        title: 'Mad March',
        body: (
          <>
            <p>
              Every February and March the Fringe, the Adelaide Festival and WOMADelaide arrive at once, the parklands
              fill with tents and the city stays up late. It is the liveliest time of year for conversation too:
              something to see every night and plenty to talk about afterwards.
            </p>
            <h3>Wine country weekends</h3>
            <p>
              The Barossa, McLaren Vale and the Adelaide Hills are all within an hour, and a weekend among the cellar
              doors is a local habit. A flirty message between tastings, a longer chat on the drive home: Adelaide
              conversation pairs well with a good red.
            </p>
          </>
        ),
      },
      {
        title: 'Glenelg evenings',
        body: (
          <p>
            The tram runs from the city to Glenelg, where Jetty Road leads down to the beach and the sunsets are
            worth the trip. Henley Beach and Semaphore are quieter alternatives. Bring that seaside ease to your
            openers: relaxed, warm and a little teasing is the Adelaide way.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Can I use MatureFlirt in the Adelaide Hills or the Barossa?',
        a: <p>Yes. Members chat from Mount Barker to Gawler and down to Victor Harbor. MatureFlirt works in any phone browser.</p>,
      },
      noCard,
      disclosure('Adelaide'),
      pricing,
    ],
    cta: { title: 'Pour a glass and say hello', text: 'Sign up free and your first messages are free.', button: 'Start chatting in Adelaide' },
  },
  '/gold-coast': {
    path: '/gold-coast',
    slug: 'gold-coast',
    name: 'Gold Coast',
    state: 'QLD',
    teaser: 'Burleigh sunsets, Broadbeach dining and sunshine all winter.',
    kicker: 'Sun, surf and confidence',
    h1: 'Mature chat on the Gold Coast',
    intro: (
      <p>
        The Gold Coast is seventy kilometres of beaches, high-rises and warm winters, and it has always attracted
        people who enjoy life. Many of its mature women moved here for exactly that reason: the sun, the ocean and
        a lifestyle with no apologies attached. MatureFlirt brings Gold Coast cougar chat to your phone, free to join
        and with your first messages free.
      </p>
    ),
    sections: [
      {
        title: 'From Surfers Paradise to Coolangatta',
        body: (
          <>
            <p>
              Surfers Paradise has the lights and the late nights, Broadbeach the restaurants and the casino, and
              Burleigh Heads the bars where the whole coast seems to gather for the sunset over the point. Further
              south, Palm Beach, Currumbin and Coolangatta are a little quieter, with surf clubs and long beach walks
              at the end of the day.
            </p>
            <p>
              The light rail along the coast and a coffee at the beach are prime chatting time, and so is any
              evening after a swim.
            </p>
          </>
        ),
      },
      {
        title: 'The hinterland and the winter sun',
        body: (
          <>
            <p>
              Behind the beaches, Tamborine Mountain and Springbrook offer rainforest, wineries and cool air. Locals
              head up for a weekend lunch and come back with the sunset behind them, a lovely time to pick up a
              conversation where it was left.
            </p>
            <h3>Why the coast suits chat</h3>
            <p>
              Winter here is warm enough for the beach, and the evenings are long and easy. People on the Gold Coast
              tend to be open and sociable, and conversations on MatureFlirt follow suit.
            </p>
          </>
        ),
      },
      {
        title: 'Openers with a bit of sparkle',
        body: (
          <p>
            Gold Coast women like confidence and a sense of fun. Ask what she loves most about living by the ocean,
            tease her gently about the tourists, or send a flirt if you want to keep it simple. Photos can be
            exchanged with credits once your free messages are used.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Can I chat from the Tweed or the hinterland?',
        a: <p>Yes. Members chat from Tweed Heads to Coomera and up in the hinterland too. All you need is your phone.</p>,
      },
      privacy,
      disclosure('Gold Coast'),
    ],
    cta: { title: 'Sunshine and a good conversation', text: 'Join free, and your first messages are free.', button: 'Start chatting on the Gold Coast' },
  },
  '/newcastle': {
    path: '/newcastle',
    slug: 'newcastle',
    name: 'Newcastle',
    state: 'NSW',
    teaser: 'Ocean baths at dawn, Darby Street dinners and Hunter Valley weekends.',
    kicker: 'Harbour, beaches and the Hunter',
    h1: 'Mature chat in Newcastle',
    intro: (
      <p>
        Newcastle has reinvented itself from a steel town into one of the most liveable small cities in Australia,
        with surf beaches at the end of the main street and the Hunter Valley vineyards an hour away. Its mature
        women are grounded, funny and refreshingly unpretentious. MatureFlirt brings that warmth to MILF chat on
        your phone, with free signup and your first messages free.
      </p>
    ),
    sections: [
      {
        title: 'Darby Street, the foreshore and Hunter Street',
        body: (
          <>
            <p>
              Darby Street in Cooks Hill is the city&apos;s favourite strip for dinner and a glass of wine. The
              Honeysuckle foreshore has restaurants looking over the harbour, where the coal ships come and go past
              Nobbys lighthouse, and the light rail now runs along Hunter Street through a revived city centre.
            </p>
            <p>
              Newcastle is compact enough that an evening can end anywhere, and often it ends with a conversation on
              the phone.
            </p>
          </>
        ),
      },
      {
        title: 'Ocean baths and the Bathers Way',
        body: (
          <>
            <p>
              The ocean baths at Newcastle and Merewether, and the Bogey Hole cut into the rocks below the cliffs,
              are where the city starts its day. The Bathers Way walk links the beaches from Nobbys to Merewether,
              and a coffee at the end of it is a good moment for a playful message.
            </p>
            <h3>Hunter Valley weekends</h3>
            <p>
              On a weekend the vineyards around Pokolbin are a short drive away. Long lunches, cellar doors and a
              slow drive home give a conversation room to grow.
            </p>
          </>
        ),
      },
      {
        title: 'A friendly start',
        body: (
          <p>
            Newcastle values honesty over show. Introduce yourself plainly, ask about her favourite beach or her
            pick of the Hunter wineries, and keep the tone light. A flirt is an easy first step if you prefer to let
            her make the next move.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Does it work in Maitland and Lake Macquarie?',
        a: <p>Yes. Members chat from Port Stephens to Lake Macquarie and out to Maitland and Cessnock. All you need is a phone with a signal.</p>,
      },
      noCard,
      disclosure('Newcastle'),
      pricing,
    ],
    cta: { title: 'From the ocean baths to your inbox', text: 'Create your free profile; your first messages are free.', button: 'Start chatting in Newcastle' },
  },
  '/canberra': {
    path: '/canberra',
    slug: 'canberra',
    name: 'Canberra',
    state: 'ACT',
    teaser: 'Lakeside walks, Braddon bars and frosty nights made for staying in.',
    kicker: 'The bush capital',
    h1: 'Mature chat in Canberra',
    intro: (
      <p>
        Canberra is a planned city of lakes, bushland and national institutions, where kangaroos graze on the hills
        a few minutes from Parliament House. It is quieter than the big cities and better educated than most, and
        its mature women tend to be sharp, well read and quietly confident. MatureFlirt brings Canberra flirt chat
        to your phone: free to join, with your first messages free.
      </p>
    ),
    sections: [
      {
        title: 'Braddon, Kingston and NewActon',
        body: (
          <>
            <p>
              Lonsdale Street in Braddon is where Canberra eats, drinks and people-watches. The Kingston foreshore has
              restaurants on the water, and NewActon, between the city and the lake, has the hotels, small bars and
              cinema that give the capital its after-work life. Manuka&apos;s cafés and Dickson&apos;s dumpling houses
              round out the map.
            </p>
            <p>
              Because the city is spread out, many evenings end early and at home, which leaves time for a good
              conversation.
            </p>
          </>
        ),
      },
      {
        title: 'The lake, the galleries and Floriade',
        body: (
          <>
            <p>
              A walk or cycle around Lake Burley Griffin is a Canberra ritual, as is an afternoon at the National
              Gallery or the Portrait Gallery. Every spring Floriade fills Commonwealth Park with flowers, and in
              autumn the Enlighten festival lights up the national buildings.
            </p>
            <h3>Frosty nights</h3>
            <p>
              Canberra winters bring hard frosts and clear, cold nights. With the heater on and the city asleep,
              those evenings are some of the busiest on MatureFlirt.
            </p>
          </>
        ),
      },
      {
        title: 'Conversation in the capital',
        body: (
          <p>
            A city of public servants, academics and diplomats appreciates a well-chosen word. Be thoughtful in your
            opener, show a little curiosity about her work or her favourite exhibition, and let some wit through.
            Discretion matters in a town this size, so you chat under a nickname.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Can I chat from Queanbeyan or the outer suburbs?',
        a: <p>Yes. Members chat from Gungahlin to Tuggeranong and across the border in Queanbeyan. MatureFlirt runs in any phone browser.</p>,
      },
      privacy,
      disclosure('Canberra'),
      pricing,
    ],
    cta: { title: 'Warm conversation for cold Canberra nights', text: 'Sign up free and send your first messages free.', button: 'Start chatting in Canberra' },
  },
  '/sunshine-coast': {
    path: '/sunshine-coast',
    slug: 'sunshine-coast',
    name: 'Sunshine Coast',
    state: 'QLD',
    teaser: 'Noosa elegance, Mooloolaba evenings and hinterland markets.',
    kicker: 'Noosa to Caloundra',
    h1: 'Mature chat on the Sunshine Coast',
    intro: (
      <p>
        The Sunshine Coast is where many Australians dream of ending up: national parks running down to the beach,
        relaxed towns and a pace that never feels rushed. The mature women who live here chose it on purpose, and
        they bring the same easy confidence to a conversation. MatureFlirt brings Sunshine Coast cougar chat to your
        phone, free to join and with your first messages free.
      </p>
    ),
    sections: [
      {
        title: 'Hastings Street, Mooloolaba and Caloundra',
        body: (
          <>
            <p>
              Hastings Street in Noosa is the coast at its most elegant, with boutiques, fine dining and the walk
              into Noosa National Park at the end of the street. Mooloolaba&apos;s Esplanade has the surf club and
              the evening crowd, Maroochydore the business end of the coast, and Caloundra the calm beaches and
              Bulcock Street&apos;s cafés.
            </p>
            <p>
              Evenings here are unhurried, and so are the conversations: plenty of time for a flirt to become a long
              chat.
            </p>
          </>
        ),
      },
      {
        title: 'Markets and the hinterland',
        body: (
          <>
            <p>
              On Wednesdays and Saturdays the Eumundi Markets draw half the coast, and the hinterland towns of
              Maleny and Montville look out over the Glass House Mountains. A weekend drive up the range and a
              lunch with a view is the local idea of a perfect day.
            </p>
            <h3>The sea-change crowd</h3>
            <p>
              Many people here moved up from Sydney or Melbourne for a better life, and they are open to new
              conversations. That makes the Sunshine Coast a friendly place to chat.
            </p>
          </>
        ),
      },
      {
        title: 'Keep it easy',
        body: (
          <p>
            The coast does not like pressure. A relaxed hello, a question about her favourite beach or her market
            finds, and a little gentle teasing are all you need. Send a flirt to say hello without words, and use
            credits for messages and photos once the free ones are gone.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Does MatureFlirt work in Gympie or the hinterland?',
        a: <p>Yes. Members chat from Gympie and Noosa down to Caloundra and inland to Nambour and Maleny. All you need is a signal.</p>,
      },
      noCard,
      disclosure('Sunshine Coast'),
    ],
    cta: { title: 'Slow evenings, warm chat', text: 'Join free; your first messages are free.', button: 'Start chatting on the Sunshine Coast' },
  },
  '/central-coast': {
    path: '/central-coast',
    slug: 'central-coast',
    name: 'Central Coast',
    state: 'NSW',
    teaser: 'Terrigal mornings, Avoca sunsets and the train ride to Sydney.',
    kicker: 'Between the city and the sea',
    h1: 'Mature chat on the Central Coast',
    intro: (
      <p>
        The Central Coast sits between Sydney and Newcastle, a stretch of beaches, lakes and national parks that
        manages to feel a world away from both. Its mature women are a mix of lifelong locals and Sydneysiders who
        traded the traffic for the water, and most of them are relaxed, outgoing and happy to chat. MatureFlirt
        brings that easy charm to MILF chat on your phone, with free signup and your first messages free.
      </p>
    ),
    sections: [
      {
        title: 'Terrigal, Avoca and The Entrance',
        body: (
          <>
            <p>
              Terrigal is the coast&apos;s social centre, with The Haven, the Skillion headland and a beachfront strip
              of cafés and bars. Avoca Beach has the old picture theatre and some of the best sunsets on the coast,
              and The Entrance gathers a crowd every afternoon to watch the pelicans being fed. Ettalong and Woy
              Woy, on the water at Brisbane Water, have a quieter village feel.
            </p>
            <p>
              On a summer evening the whole coast seems to be outdoors, and many conversations begin with the beach
              still in sight.
            </p>
          </>
        ),
      },
      {
        title: 'The commute and the weekend',
        body: (
          <>
            <p>
              Thousands of locals take the train to Sydney every day, and the hour and a half along the Hawkesbury
              River is ideal for a flirty chat. At the weekend it is Bouddi National Park, the Tuggerah Lakes or a
              long lunch in Gosford instead.
            </p>
            <h3>Coastal life</h3>
            <p>
              Life on the Central Coast moves to the rhythm of the tides and the school run. Evenings are quiet and
              homely, which leaves plenty of room for a conversation that grows warmer by the message.
            </p>
          </>
        ),
      },
      {
        title: 'Saying hello',
        body: (
          <p>
            People here are approachable and like a friendly, natural opener. Ask about her favourite beach walk or
            how she spends her weekends, add a touch of humour, and see where it goes. A flirt is a simple way to
            show interest.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Can I chat from Wyong, Gosford or the peninsula?',
        a: <p>Yes. Members chat from the Woy Woy peninsula to Lake Macquarie&apos;s southern shores. MatureFlirt runs in any phone browser.</p>,
      },
      privacy,
      disclosure('Central Coast'),
      pricing,
    ],
    cta: { title: 'Coastal evenings, warmer conversation', text: 'Create your free profile; your first messages are free.', button: 'Start chatting on the Central Coast' },
  },
};
